import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { wallpaperService } from '../services/wallpaperService';
import { historyService, WallpaperHistoryItem } from '../services/historyService';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: calc(100vh - 200px);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 30px;
  color: var(--color-black);
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  background-color: ${props => props.$active ? 'var(--color-black)' : 'var(--color-white)'};
  color: ${props => props.$active ? 'var(--color-white)' : 'var(--color-black)'};
  border: 2px solid var(--color-black);
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  
  &:hover {
    background-color: ${props => props.$active ? 'var(--color-white)' : 'var(--color-black)'};
    color: ${props => props.$active ? 'var(--color-black)' : 'var(--color-white)'};
  }
`;

const WallpaperGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const WallpaperCard = styled.div`
  background: var(--color-white);
  border: 2px solid var(--color-black);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
  border-radius: 0;
  
  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-blue);
  }
`;

const WallpaperImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const WallpaperInfo = styled.div`
  padding: 15px;
`;

const WallpaperTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-black);
  margin-bottom: 8px;
`;

const WallpaperMeta = styled.div`
  font-size: 0.8rem;
  color: var(--color-gray);
  margin-bottom: 10px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $delete?: boolean }>`
  background-color: ${props => props.$delete ? '#dc2626' : 'var(--color-black)'};
  color: var(--color-white);
  border: 2px solid ${props => props.$delete ? '#dc2626' : 'var(--color-black)'};
  padding: 6px 12px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  
  &:hover {
    background-color: ${props => props.$delete ? 'var(--color-white)' : 'var(--color-white)'};
    color: ${props => props.$delete ? '#dc2626' : 'var(--color-black)'};
    border-color: ${props => props.$delete ? '#dc2626' : 'var(--color-black)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--color-gray);
`;


const EmptyTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: var(--color-black);
`;

const EmptyText = styled.p`
  font-size: 1rem;
  margin-bottom: 20px;
`;

const EmptyButton = styled.button`
  background-color: var(--color-black);
  color: var(--color-white);
  border: 2px solid var(--color-black);
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;
  
  &:hover {
    background-color: var(--color-white);
    color: var(--color-black);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: var(--color-gray);
`;

const HistoryScreen: React.FC = () => {
  const [wallpapers, setWallpapers] = useState<WallpaperHistoryItem[]>([]);
  const [filter, setFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await historyService.getHistory();
        console.log('Loaded history from database:', history);
        setWallpapers(history);
      } catch (error) {
        console.error('加载历史记录失败:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, []);

  const filteredWallpapers = wallpapers.filter(wallpaper => {
    if (filter === 'all') return true;
    if (filter === 'generated') return wallpaper.style === 'generated' || wallpaper.style === 'controlnet-structure';
    if (filter === 'online') return wallpaper.style === 'online' || wallpaper.style === 'search';
    return wallpaper.style === filter;
  });

  const handleDownload = (wallpaper: WallpaperHistoryItem) => {
    wallpaperService.downloadWallpaper(wallpaper.fullImageUrl, `${wallpaper.title}.png`);
    historyService.incrementDownloads(wallpaper.id);
    // 更新本地状态
    setWallpapers(wallpapers.map(w => 
      w.id === wallpaper.id ? { ...w, downloads: w.downloads + 1 } : w
    ));
  };

  const handleDelete = async (wallpaperId: string) => {
    if (window.confirm('Are you sure you want to delete this wallpaper?')) {
      try {
        await historyService.deleteWallpaper(wallpaperId);
        setWallpapers(wallpapers.filter(w => w.id !== wallpaperId));
      } catch (error) {
        console.error('Error deleting wallpaper:', error);
        alert('Failed to delete wallpaper. Please try again.');
      }
    }
  };

  const handleShare = (wallpaper: WallpaperHistoryItem) => {
    if (navigator.share) {
      navigator.share({
        title: wallpaper.title,
        text: `Check out this wallpaper I generated: ${wallpaper.title}`,
        url: wallpaper.fullImageUrl
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(wallpaper.fullImageUrl);
      alert('Wallpaper URL copied to clipboard!');
    }
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear all wallpaper history?')) {
      try {
        await historyService.clearHistory();
        setWallpapers([]);
      } catch (error) {
        console.error('Error clearing history:', error);
        alert('Failed to clear history. Please try again.');
      }
    }
  };

  if (isLoading) {
    return (
      <Container>
        <Title>History</Title>
        <LoadingState>
          <div>Loading...</div>
        </LoadingState>
      </Container>
    );
  }

  if (wallpapers.length === 0) {
    return (
      <Container>
        <Title>History</Title>
        <EmptyState>
          <EmptyTitle>No History</EmptyTitle>
          <EmptyText>You haven't generated any wallpapers yet</EmptyText>
          <EmptyButton onClick={() => window.location.href = '/wallpaper'}>
            Start Generating
          </EmptyButton>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Title>History</Title>
      
      <FilterContainer>
        <FilterButton 
          $active={filter === 'all'} 
          onClick={() => setFilter('all')}
        >
          All
        </FilterButton>
        <FilterButton 
          $active={filter === 'generated'} 
          onClick={() => setFilter('generated')}
        >
          Generated
        </FilterButton>
        <FilterButton 
          $active={filter === 'online'} 
          onClick={() => setFilter('online')}
        >
          Online
        </FilterButton>
      </FilterContainer>

      <WallpaperGrid>
        {filteredWallpapers.map((wallpaper) => (
          <WallpaperCard key={wallpaper.id}>
            <WallpaperImage 
              src={wallpaper.thumbnail || wallpaper.fullImageUrl} 
              alt={wallpaper.title}
              onError={(e) => {
                // 如果缩略图加载失败，使用完整图片URL
                if (wallpaper.thumbnail !== wallpaper.fullImageUrl) {
                  (e.target as HTMLImageElement).src = wallpaper.fullImageUrl;
                }
              }}
            />
            <WallpaperInfo>
              <WallpaperTitle>{wallpaper.title}</WallpaperTitle>
              <WallpaperMeta>
                Created: {wallpaper.createdAt}
                <br />
                Size: {wallpaper.dimensions.width}×{wallpaper.dimensions.height}
              </WallpaperMeta>
              <ActionButtons>
                <ActionButton onClick={() => handleDownload(wallpaper)}>
                  Download
                </ActionButton>
                <ActionButton $delete onClick={() => handleDelete(wallpaper.id)}>
                  Delete
                </ActionButton>
              </ActionButtons>
            </WallpaperInfo>
          </WallpaperCard>
        ))}
      </WallpaperGrid>
    </Container>
  );
};

export default HistoryScreen;
